<template>
  <div class="requests-table-wrapper">
    <h2 class="table-title">merchant requests</h2>
    <loading-icon v-if="requestsLoading" />

    <sb-table v-else-if="hasData" :columns="columns" :rows="requests">
      <template #cell="{row, column}">
        <div v-if="column.type === 'text'" :class="column.class" class="cell">
          {{ getFormattedValue(column.key, row[column.key]) }}
        </div>
        <div v-if="column.key === 'created_ad'">
          <button v-if="row.request_type !== 'InventoryRequest'" class="button create-ad" @click="createAd(row)">create ad</button>
          <div v-else/>
        </div>
        <div v-if="column.key === 'dismiss'">
          <button class="button dismiss" @click="showModal(row)">dismiss</button>
        </div>
      </template>
    </sb-table>

    <div v-else>{{ requestsError || 'no requests available' }}</div>

    <modal v-if="showDismissModal"
      :showActionButton="false"
      showClose
      wrapperClasses="dismiss-modal"
      @close="closeModal">
      <h2 slot="body">are you sure you want to dismiss the merchant request?</h2>
      <div slot="footer" class="buttons-wrapper flex">
        <button class="dismiss-button" @click="dismissedRequest">dismiss</button>
        <button class="cancel-button" @click="closeModal">cancel</button>
      </div>
    </modal>

    <pagination
      v-if="showPagination"
      :currentPage="currentPage"
      :totalPageCount="totalPages"
      showArrows
      @changePage="changePage"/>
  </div>
</template>

<script>
import { brandsAPI } from '../../services/api/brands.ts'
import { calculateTotalPagesUtil } from '../../utilities'
import { format } from 'date-fns'
import { mapState } from 'vuex'
import LoadingIcon from '../Shared/LoadingIcon.vue'
import Modal from '../Shared/Modal.vue'
import Pagination from '../Shared/Pagination.vue'

export default {
  props: {
    selectedTab: Object,
    currentAdvertiserId: Number,
  },
  name: 'DashboardRequestTable',

  components: { Pagination, LoadingIcon, Modal },

  data() {
    return {
      columns: [
        { label: 'merchant', key: 'merchant', type: 'text', class: 'first-cell' },
        { label: 'date sent', key: 'date_sent', type: 'text' },
        { label: 'request type', key: 'request_type', type: 'text', class: 'last-cell' },
        { label: '', key: 'created_ad', type: 'button', class: 'action-cell' },
        { label: '', key: 'dismiss', type: 'button', class: 'action-cell' }
      ],
      currentPage: 1,
      dateFormat: 'MM/dd/yy',
      requests: null,
      requestsError: null,
      requestsLoading: false,
      requestTypes: {
        InventoryRequest: 'inventory',
        SponsorRequest: 'ad sponsor'
      },
      totalPages: null,
      showDismissModal: false,
      selectedRequest: null
    }
  },

  computed: {
    hasData() {
      return this.requests?.length && !this.requestsError
    },
    showPagination() {
      return !this.requestsLoading && this.totalPages > 1
    },
    ...mapState(['merchantRequests'])
  },

  mounted() {
    this.getRequests()
  },

  methods: {
    changePage(page) {
      this.currentPage = page
      this.getRequests()
    },
    closeModal() {
      this.showDismissModal = false
      this.selectedRequest = null
    },
    createAd(request) {
      this.$router.push({
        name: 'AdEdit',
        params: { extraData: { marketplace_sponsor_request_id: request.id }}
      })
    },
    async dismissedRequest() {
      try {
        this.requestsLoading = true
        this.requestsError = null
        await brandsAPI.dismissMerchantRequest(this.currentAdvertiserId, this.selectedRequest.id)
        this.requests = this.requests.filter(({id}) => id !== this.selectedRequest.id)
        this.$store.commit('SET_MERCHANT_REQUESTS', { merchantRequests: this.requests })
      } catch(e) {
        this.requestsError = e
      } finally {
        this.closeModal()
        this.requestsLoading = false
      }
    },
    async getRequests() {
      try {
        this.requestsLoading = true
        this.requestsError = null
        await this.$store.dispatch('FETCH_MERCHANT_REQUESTS', { advertiserID: this.currentAdvertiserId, page: this.currentPage })
        this.requests = this.merchantRequests.requests
        const pagination = this.merchantRequests?.pagination
        if (pagination) {
          this.totalPages = pagination?.total_pages || 1
        }
      } catch(e) {
        this.requestsError = e
      } finally {
        this.requestsLoading = false
      }
    },
    getFormattedValue(key, value) {
      let finalValue = value
      if (key === 'date_sent') finalValue = format(new Date(value), this.dateFormat)
      else if (key === 'request_type') finalValue = this.requestTypes[value]
      return finalValue
    },
    showModal(request) {
      this.selectedRequest = request
      this.showDismissModal = true
    }
  },
}
</script>

<style lang="sass">
@import "../../assets/sass/colors"

.requests-table-wrapper
  .table-title
    margin: 2rem 0
  .grid-container
    // Have to use !important here as the default attribute is injected directly to sb-table
    // so without !important, this attribute would be overwritten every time
    // Could be removed once direct style injection on sb-table is removed
    .grid-header, .grid-content
      grid-template-columns: 2fr 2fr 2fr 1fr 1fr !important
  .grid-header
    margin-bottom: 2em
  .grid-content
    &:not(:last-child)
      margin-bottom: 8px
    .cell
      border-top: 1px solid $primary-color
      border-bottom: 1px solid $primary-color
      padding: 8px
    .first-cell
      border-radius: 1em 0 0 1em
      border-left: 1px solid $primary-color
    .last-cell
      border-radius: 0 1em 1em 0
      border-right: 1px solid $primary-color
  .button
    font-size: 1em
  .button.dismiss
    position: unset
    background: none
    color: $primary-color
    padding: unset
  .button.create-ad
    position: unset
    border-radius: 10px
    padding: .4rem 1rem
  .dismiss-modal .buttons-wrapper
    justify-content: space-around
    margin-top: 3rem
    button
      border-radius: 8px
      border: none
      cursor: pointer
      font-family: 'AileronThin'
      font-size: 1em
      padding: .5em 0
      transition: background-color 0.25s ease-out, color 0.25s ease-out
      width: 100px
      color: white
    .dismiss-button
      background-color: $primary-color
    .cancel-button
      background-color: $negative-color
</style>
